/*
  File: src/pages/ProfilePage.jsx
  Purpose: User profile page to edit basic info and default shipping address.
  Responsibilities:
  - Fetch current user and address; allow updates via /users/me with shipping_address.
  - Provide simple form controls and a save action with feedback.
*/
import React, { useEffect, useState, useRef } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { apiClient } from '../auth/api';

export default function ProfilePage() {
  const [user, setUser] = useState({ name: '', email: '' });
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [addr, setAddr] = useState({ full_name: '', line1: '', line2: '', city: '', state: '', postal_code: '', country: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const me = await apiClient.get('/users/me');
        if (me?.data) {
          setUser({ name: me.data.name || '', email: me.data.email || '' });
          if (me.data.avatar_url) setAvatarPreview(me.data.avatar_url);
        }
      } catch { }
      try {
        const { data } = await apiClient.get('/shipping-addresses');
        if (data && data.item) setAddr({
          full_name: data.item.full_name || '',
          line1: data.item.line1 || '',
          line2: data.item.line2 || '',
          city: data.item.city || '',
          state: data.item.state || '',
          postal_code: data.item.postal_code || '',
          country: data.item.country || '',
          phone: data.item.phone || '',
        });
      } catch { }
    })();
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      // Upload avatar first if selected
      let uploadedAvatarUrl = null;
      if (avatarFile) {
        const form = new FormData();
        form.append('image', avatarFile);
        const res = await apiClient.post('/users/me/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploadedAvatarUrl = res?.data?.avatar_url || null;
      }
      console.log('Saving profile:', { name: user.name, email: user.email, shipping_address: addr });
      const response = await apiClient.post('/users/me', {
        name: user.name,
        email: user.email,
        avatar_url: uploadedAvatarUrl || undefined,
        shipping_address: addr
      });
      console.log('Profile saved successfully:', response.data);
      // Refresh /users/me so global auth context picks up new avatar_url
      try {
        const me = await apiClient.get('/users/me');
        if (me?.data?.avatar_url) setAvatarPreview(me.data.avatar_url);
        // Also push to localStorage so Navbar sees it immediately
        try { localStorage.setItem('auth:user', JSON.stringify(me.data)); } catch {}
        // best-effort dispatch to any listeners
        window.dispatchEvent(new StorageEvent('storage', { key: 'auth:user', newValue: JSON.stringify(me.data) }));
      } catch {}
      alert('Profile saved successfully!');
    } catch (error) {
      console.error('Failed to save profile:', error);
      console.error('Error details:', error.response?.data || error.message);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen pt-14 sm:pt-16">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Your Profile</h1>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="relative rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl">
            <h2 className="text-lg font-semibold mb-4">Basic Info</h2>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-full overflow-hidden border border-white/10 bg-white/10 flex items-center justify-center">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-xs text-white/60">No avatar</div>
                  )}
                </div>
                <div className="text-xs">
                  <span className="inline-block mb-1 text-white/80">Upload avatar</span>
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (!f) return;
                        setAvatarFile(f);
                        try { setAvatarPreview(URL.createObjectURL(f)); } catch {}
                      }}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="btn-primary rounded-lg px-3 py-1.5 text-xs"
                    >
                      Choose photo
                    </button>
                    {avatarFile && (
                      <span className="text-white/60 truncate max-w-[10rem]" title={avatarFile.name}>{avatarFile.name}</span>
                    )}
                  </div>
                  <div className="mt-1 text-[11px] text-white/50">PNG or JPG, up to 5MB.</div>
                </div>
              </div>
              <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Full name" value={user.name} onChange={(e) => setUser(u => ({ ...u, name: e.target.value }))} />
              <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Email" value={user.email} onChange={(e) => setUser(u => ({ ...u, email: e.target.value }))} />
            </div>
          </section>

          <section className="relative rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl">
            <h2 className="text-lg font-semibold mb-4">Default Shipping Address</h2>
            <div className="grid grid-cols-1 gap-3">
              <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Full name" value={addr.full_name} onChange={(e) => setAddr(a => ({ ...a, full_name: e.target.value }))} />
              <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Address line 1" value={addr.line1} onChange={(e) => setAddr(a => ({ ...a, line1: e.target.value }))} />
              <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Address line 2 (optional)" value={addr.line2} onChange={(e) => setAddr(a => ({ ...a, line2: e.target.value }))} />
              <div className="grid grid-cols-2 gap-3">
                <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="City" value={addr.city} onChange={(e) => setAddr(a => ({ ...a, city: e.target.value }))} />
                <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="State/Province" value={addr.state} onChange={(e) => setAddr(a => ({ ...a, state: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Postal code" value={addr.postal_code} onChange={(e) => setAddr(a => ({ ...a, postal_code: e.target.value }))} />
                <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Country" value={addr.country} onChange={(e) => setAddr(a => ({ ...a, country: e.target.value }))} />
              </div>
              <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Phone (optional)" value={addr.phone} onChange={(e) => setAddr(a => ({ ...a, phone: e.target.value }))} />
            </div>
          </section>

          <div className="lg:col-span-2 flex items-center justify-end gap-3">
            <button onClick={save} disabled={saving} className={`group relative ${saving ? 'opacity-60 cursor-not-allowed' : ''} bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-6 py-3 rounded-full font-semibold text-lg transition-all duration-300 ${saving ? '' : 'transform hover:scale-105 hover:shadow-xl'}`}>{saving ? 'Saving…' : 'Save Profile'}</button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}


